import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useOutletContext } from "react-router-dom";
import { HomeOutlinedIcon, FolderIcon, AddIcon } from "../../../icons";
import GlobalLoader from "../../common/GlobalLoader";
import {
  getMaterialFolders,
  createMaterialSubtopicFolder,
  updateMaterialFolder,
  deleteMaterialFolder,
} from "../../../helper/apicalls/materials";
import {
  getAdminMaterialFolderInInstitution,
  getMaterialFoldersInstitution,
  createMaterialFolderInstitution,
  updateMaterialFolderInstitution,
  deleteMaterialFolderInstitution,
} from "../../../helper/apicalls/institution/materials";
import CreateFolderPopup from "../../common/CreateFolderPopup";
import DeleteConfirmPopUp from "../../common/deleteConfirmPopUp";
import "./../../../styles/admin/questionBank.scss";
import Cookies from "js-cookie";

const MaterialsSubtopicFiles = () => {
  const { courseKey, subjectKey, topicKey, subtopicKey } = useParams();
  const navigate = useNavigate();
  const { setHeaderName } = useOutletContext();
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateFolderPopup, setShowCreateFolderPopup] = useState(false);
  const [folderMenuOpenId, setFolderMenuOpenId] = useState(null);
  const [editFolder, setEditFolder] = useState(null);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState(null);
  const [breadcrumbLinks, setBreadcrumbLinks] = useState([
    { icon: <HomeOutlinedIcon />, path: "/admin/materials" },
    { label: "Course " + courseKey },
    { label: "Subject " + subjectKey },
    { label: "Topic " + topicKey },
    { label: "Subtopic " + subtopicKey, active: true },
  ]);
  const role = Cookies.get("role");
  const permissions = Cookies.get("permissions");
  const permissionsData = permissions ? JSON.parse(permissions) : [];
  const materialPermission = Array.isArray(permissionsData)
    ? permissionsData.find((p) => p?.permission_type === "material")
    : null;
  const hasWritePermission =
    role === "admin" || (materialPermission ? !!materialPermission.write : false);

  useEffect(() => {
    if (!folderMenuOpenId) return;

    const handleClickOutside = (event) => {
      const menus = document.querySelectorAll(
        ".folder-menu, .folder-menu-button"
      );
      for (let i = 0; i < menus.length; i += 1) {
        const el = menus[i];
        if (el && el.contains(event.target)) {
          return;
        }
      }

      setFolderMenuOpenId(null);
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [folderMenuOpenId]);

  const fetchFolders = async () => {
    try {
      setLoading(true);

      let data = [],
        adminFoldersData = [],
        institutionFoldersData = [];

      if (role === "institution" || role === "teacher") {
        adminFoldersData = await getAdminMaterialFolderInInstitution(
          "subtopic",
          subtopicKey
        );
        institutionFoldersData = await getMaterialFoldersInstitution(
          "subtopic",
          subtopicKey
        );

        data = [
          ...adminFoldersData.map((folder) => ({ ...folder, is_admin: true })),
          ...institutionFoldersData.map((folder) => ({
            ...folder,
            is_admin: false,
          })),
        ];
      } else {
        data = await getMaterialFolders("subtopic", subtopicKey);
      }

      setFolders(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching folders for subtopic materials:", err);
      setFolders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFolderClick = (folder) => {
    if (!folder?.id) return;
    const portalBase = role === "admin" ? "/admin" : role === "teacher" ? "/teacher" : "/institution";
    navigate(`${portalBase}/materials/folder/${folder.id}`, {
      state: {
        level: "subtopic",
        courseKey: courseKey,
        subjectKey: subjectKey,
        topicKey: topicKey,
        subtopicKey: subtopicKey,
        folderName: folder.name,
        is_admin: folder.is_admin || false,
      },
    });
  };

  const handleAddFolder = () => {
    setShowCreateFolderPopup(true);
  };

  const handleFolderMenuToggle = (id, e) => {
    e.stopPropagation();
    setFolderMenuOpenId((prev) => (prev === id ? null : id));
  };

  const handleFolderEdit = (folder, e) => {
    e.stopPropagation();
    setEditFolder(folder);
  };

  const handleFolderDelete = (folder, e) => {
    e.stopPropagation();
    setFolderToDelete(folder);
    setIsConfirmDeleteOpen(true);
  };

  useEffect(() => {
    const updatedLinks = [...breadcrumbLinks];
    const portalBase = role === "admin" ? "/admin" : role === "teacher" ? "/teacher" : "/institution";
    if (role === "institution" || role === "teacher") {
      updatedLinks[0].path = `${portalBase}/materials`;
    } else {
      updatedLinks[0].path = "/admin/materials";
    }
    setBreadcrumbLinks(updatedLinks);

    fetchFolders();
    setHeaderName("Materials - Subtopic Files");
  }, [courseKey, subjectKey, topicKey, subtopicKey]);

  return (
    <>
      <GlobalLoader visible={loading} text="Loading..." />
      <div className="question-bank-container">
        <div className="qb-card">
          <div className="qb-card-header">
            <span>Subtopic Materials Folders</span>
            <div className="qb-card-actions">
              {hasWritePermission && (
                <button
                  type="button"
                  className="qb-add-btn"
                  onClick={handleAddFolder}
                >
                  <AddIcon className="action-button-icon" />
                  <span>Add Folder</span>
                </button>
              )}
            </div>
          </div>

          <div className="folder-grid">
            {!loading &&
              folders?.map((folder) => {
                const id = folder.id || folder.key;
                return (
                  <div
                    key={id}
                    className="folder-item"
                    onClick={() => handleFolderClick(folder)}
                  >
                    <div className="folder-icon">
                      <FolderIcon style={{ color: "var(--folder-bg)" }} />
                    </div>
                    <div className="folder-name">{folder.name}</div>
                    {(role === "institution" || role === "teacher") && folder.is_admin && (
                      <div className="qb-default-badge">Default</div>
                    )}
                    {!folder.is_admin && hasWritePermission && (
                      <button
                        type="button"
                        className="folder-menu-button"
                        onClick={(e) => handleFolderMenuToggle(id, e)}
                      >
                        ⋮
                      </button>
                    )}
                    {!folder.is_admin && hasWritePermission && folderMenuOpenId === id && (
                      <div
                        className="folder-menu"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div
                          className="folder-menu-item"
                          onClick={(e) => handleFolderEdit(folder, e)}
                        >
                          Edit
                        </div>
                        <div
                          className="folder-menu-item --danger"
                          onClick={(e) => handleFolderDelete(folder, e)}
                        >
                          Delete
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      </div>

     {showCreateFolderPopup&& <CreateFolderPopup
        open={showCreateFolderPopup}
        handleClose={() => setShowCreateFolderPopup(false)}
        title="subtopic"
        contextKey={subtopicKey}
        mode="create"
        onSave={async (payload) => {
          try {
            setLoading(true);
            if (role === "institution" || role === "teacher") {
              await createMaterialFolderInstitution({
                title: payload.title,
                key: payload.key,
                name: payload.name,
              });
            } else {
              await createMaterialSubtopicFolder(
                payload.title,
                payload.key,
                payload.name
              );
            }

            const roleRefresh = Cookies.get("role");
            let adminFoldersData = [],
              institutionFoldersData = [],
              foldersData = [];
            if (roleRefresh === "institution" || roleRefresh === "teacher") {
              adminFoldersData = await getAdminMaterialFolderInInstitution(
                "subtopic",
                subtopicKey
              );
              institutionFoldersData = await getMaterialFoldersInstitution(
                "subtopic",
                subtopicKey
              );

              foldersData = [
                ...adminFoldersData.map((folder) => ({
                  ...folder,
                  is_admin: true,
                })),
                ...institutionFoldersData.map((folder) => ({
                  ...folder,
                  is_admin: false,
                })),
              ];
            } else {
              foldersData = await getMaterialFolders("subtopic", subtopicKey);
            }
            setFolders(Array.isArray(foldersData) ? foldersData : []);
          } catch (err) {
            console.error("Error creating subtopic materials folder:", err);
          } finally {
            setLoading(false);
          }
        }}
      />}

      {!!editFolder && (
        <CreateFolderPopup
          open={!!editFolder}
          handleClose={() => setEditFolder(null)}
          title="subtopic"
          contextKey={subtopicKey}
          mode="edit"
          initialName={editFolder?.name || ""}
          folderId={editFolder?.id}
          onSave={async (payload) => {
            try {
              if (editFolder?.is_admin) {
                console.warn("Cannot edit admin folder");
                return;
              }

              if (!payload.id) return;
              setLoading(true);
              const role = Cookies.get("role");
              if (role === "institution" || role === "teacher") {
                await updateMaterialFolderInstitution(payload.id, payload.name);
              } else {
                await updateMaterialFolder(payload.id, payload.name);
              }

              const roleRefresh = Cookies.get("role");
              let adminFoldersData = [],
                institutionFoldersData = [],
                foldersData = [];
              if (roleRefresh === "institution" || roleRefresh === "teacher") {
                adminFoldersData = await getAdminMaterialFolderInInstitution(
                  "subtopic",
                  subtopicKey
                );
                institutionFoldersData = await getMaterialFoldersInstitution(
                  "subtopic",
                  subtopicKey
                );

                foldersData = [
                  ...adminFoldersData.map((folder) => ({
                    ...folder,
                    is_admin: true,
                  })),
                  ...institutionFoldersData.map((folder) => ({
                    ...folder,
                    is_admin: false,
                  })),
                ];
              } else {
                foldersData = await getMaterialFolders("subtopic", subtopicKey);
              }
              setFolders(Array.isArray(foldersData) ? foldersData : []);
            } catch (err) {
              console.error("Error updating subtopic materials folder:", err);
            } finally {
              setLoading(false);
              setEditFolder(null);
            }
          }}
        />
      )}

      {isConfirmDeleteOpen && (
        <DeleteConfirmPopUp
          isConfirmDeleteOpen={isConfirmDeleteOpen}
          setIsConfirmDeleteOpen={setIsConfirmDeleteOpen}
          title="Delete folder"
          message={`Are you sure you want to delete the folder "${
            folderToDelete?.name || ""
          }"? This action cannot be undone.`}
          confirmLabel="Delete"
          cancelLabel="Cancel"
          handleDelete={async () => {
            if (folderToDelete?.is_admin) {
              console.warn("Cannot delete admin folder");
              setIsConfirmDeleteOpen(false);
              return;
            }

            if (!folderToDelete?.id) {
              setIsConfirmDeleteOpen(false);
              return;
            }
            try {
              setLoading(true);
              const role = Cookies.get("role");
              if (role === "institution" || role === "teacher") {
                await deleteMaterialFolderInstitution(folderToDelete.id);
              } else {
                await deleteMaterialFolder(folderToDelete.id);
              }

              const roleRefresh = Cookies.get("role");
              let adminFoldersData = [],
                institutionFoldersData = [],
                foldersData = [];
              if (roleRefresh === "institution" || roleRefresh === "teacher") {
                adminFoldersData = await getAdminMaterialFolderInInstitution(
                  "subtopic",
                  subtopicKey
                );
                institutionFoldersData = await getMaterialFoldersInstitution(
                  "subtopic",
                  subtopicKey
                );

                foldersData = [
                  ...adminFoldersData.map((folder) => ({
                    ...folder,
                    is_admin: true,
                  })),
                  ...institutionFoldersData.map((folder) => ({
                    ...folder,
                    is_admin: false,
                  })),
                ];
              } else {
                foldersData = await getMaterialFolders("subtopic", subtopicKey);
              }
              setFolders(Array.isArray(foldersData) ? foldersData : []);
            } catch (err) {
              console.error("Error deleting subtopic materials folder:", err);
            } finally {
              setLoading(false);
              setIsConfirmDeleteOpen(false);
              setFolderToDelete(null);
            }
          }}
        />
      )}
    </>
  );
};

export default MaterialsSubtopicFiles;
